import { GET_INSTANCE_PROPS_PATH } from "apiClient/v2/inspection/inspectionInstanceApi";
import { SOURCE } from "components/containers/InspectionFormat/Content/SettingPinLayoutFrame/context/pinLayoutFrameReducer";
import {
  CommonPropertyName,
  PREFIX_COMMON,
  PREFIX_OTHER,
} from "interfaces/models/generateData";
import {
  GetInstancePropertiesReq,
  InstanceProperty,
} from "interfaces/models/inspectionInstance";
import { PinComponent } from "interfaces/models/pinComponent";
import { logDev } from "utils/logs";

export const getInspectionInstanceProperties = async (
  params: GetInstancePropertiesReq
) => {
  const url = `${process.env.REACT_APP_API_HOST_URL}${GET_INSTANCE_PROPS_PATH}`;

  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });
  const reader = response.body?.getReader();
  if (!reader) {
    return [];
  }

  const decoder = new TextDecoder("utf-8");
  const result: InstanceProperty[] = [];
  let incompleteData = ""; // Buffer to hold incomplete chunks
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    // Decode the received value and append to the buffer
    incompleteData += decoder.decode(value, { stream: true });

    // Split the buffer by newlines
    const chunks = incompleteData.split("\n");

    // Process all but the last chunk (it may be incomplete)
    for (let i = 0; i < chunks.length - 1; i++) {
      try {
        const jsonData = JSON.parse(chunks[i]);
        result.push(jsonData);
      } catch (err) {
        logDev("error");
      }
    }

    // The last chunk is either complete or incomplete; save it back to the buffer
    incompleteData = chunks[chunks.length - 1];
  }

  // Handle any remaining buffered data after the loop
  if (incompleteData.trim() !== "") {
    try {
      const jsonData = JSON.parse(incompleteData);
      result.push(jsonData);
    } catch (err) {
      logDev("Error parsing final JSON:", err, "Chunk:", incompleteData);
    }
  }

  return result;
};

export const generateSourceBimId = (category: string, name: string) => {
  return `${category}__${name}`;
};

export const getPropertyNameFromSourceBimId = (
  sourceBimId: string,
  categoryName: string
) => {
  const categoryWithAfterfix = `${categoryName}__`;
  const regex = new RegExp(
    `(${categoryWithAfterfix}|${PREFIX_COMMON}|${PREFIX_OTHER})`,
    "g"
  );

  return sourceBimId.replace(regex, "");
};

export const getInstancePropertyValueByName = (
  instance: InstanceProperty | undefined,
  propertyName: string,
  categoryName: string
) => {
  const isCommonProperty = Object.values(CommonPropertyName).includes(
    propertyName as any
  );

  if (isCommonProperty) {
    return instance?.cp?.find((i) => i.atn === propertyName)?.dv || "";
  }

  return (
    instance?.p?.find(
      (i) => instance.cn === categoryName && i.atn === propertyName
    )?.dv || ""
  );
};

export const getBimPropertyNamesInFrames = (frames: PinComponent[]) => {
  const propertyNameSet: Set<string> = new Set();
  frames.forEach((component) => {
    const linkedData = component.linkedData;

    if (linkedData?.source !== SOURCE.BIM || !linkedData) {
      return;
    }

    propertyNameSet.add(
      getPropertyNameFromSourceBimId(linkedData.key, String(linkedData.extraId))
    );
  });

  return Array.from(propertyNameSet);
};

export function formatStringProperty(input: string) {
  const items = input.slice(1, -1).split("}, {");

  return items.map((item) => {
    item = item.replace(/{|}/g, "");
    const pairs = item.split(", ");
    const obj: any = {};
    pairs.forEach((pair) => {
      const [key, value] = pair.split("=");
      obj[key] = value || "";
    });

    return {
      dv: obj.dv,
      atn: obj.atn,
    };
  });
}

export const transformInstanceProperties = (
  data: InstanceProperty[],
  properties: string[]
) => {
  const validPropertySet = new Set(properties);
  data.forEach((item) => {
    item.p = item.p.filter((i) => validPropertySet.has(i.atn));
  });
};
